import { Link, LinkProps } from '../Link/Link';
import styles from './UpgradeExperienceCard.module.scss';
import { useContext } from 'react';
import { EnvironmentContext } from '../../contexts';
import { RestrictionLevel } from '../MembershipTypeLogos/constants';
import MembershipTypesLogos from '../MembershipTypeLogos/MembershipTypesLogos';
import Tag, { TagProps } from '../Tag/Tag';
import { RenderOn } from '../RenderOn/RenderOn';
import { useScreenDetector } from '../../hooks';
import classNames from 'classnames';

type ImageType = {
  url?: string;
  alt?: string | null;
};

export interface UpgradeExperienceCardProps {
  title?: string;
  description?: string;
  backgroundImage?: ImageType;
  mobileBackgroundImage?: ImageType;
  link?: LinkProps;
  ctaText?: string;
  access?: keyof typeof RestrictionLevel;
  tags?: TagProps[];
}
export function UpgradeExperienceCard({
  tags,
  backgroundImage,
  mobileBackgroundImage,
  title,
  description,
  link,
  ctaText,
  access = 'none',
}: UpgradeExperienceCardProps) {
  const { Image } = useContext(EnvironmentContext);
  const { isMobile } = useScreenDetector();

  const ctaLink = {
    title: ctaText || link?.title || '',
    internal: !!link?.internal,
    openInNewTab: !!link?.openInNewTab,
    href: link?.href || '',
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.card}>
        {!isMobile && (
          <UpgradeBackgroundImageComponent
            className="card__background"
            backgroundImage={backgroundImage}
          />
        )}
        {isMobile && (
          <div className={styles.card__background}>
            <Image
              src={mobileBackgroundImage?.url || ''}
              alt={mobileBackgroundImage?.alt || ''}
              width={390}
              height={546}
              sizes="100vw"
            />
          </div>
        )}
        <div className={styles.card__membership}>
          <MembershipTypesLogos
            restrictionLevel={access as keyof typeof RestrictionLevel}
          />
          <RenderOn condition={!!tags?.length}>
            <div className={styles.card__membership__tags}>
              {tags?.map(tag => (
                <Tag key={tag.label?.value} {...tag} />
              ))}
            </div>
          </RenderOn>
        </div>
        <div className={styles.card__content}>
          {title && <h3>{title}</h3>}
          {!isMobile && description && (
            <p className={styles.card__content__description}>{description}</p>
          )}
          {(!!ctaLink?.href?.length || !!ctaLink?.title?.length) && (
            <div className={styles.card__content__link}>
              <Link
                className={classNames(styles.btn, styles['btn--secondary'], {
                  [styles['inactive-link']]: !ctaLink?.href?.length,
                })}
                {...ctaLink}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UpgradeExperienceCard;

const UpgradeBackgroundImageComponent = ({
  className,
  backgroundImage,
}: {
  className: string;
  backgroundImage?: ImageType;
}) => {
  const { Image } = useContext(EnvironmentContext);

  return backgroundImage ? (
    <div className={styles[className]}>
      <Image
        src={backgroundImage?.url || ''}
        alt={backgroundImage?.alt || ''}
        width={762}
        height={316}
        sizes="100vw"
      />
    </div>
  ) : null;
};
